import React from 'react';
import { Link } from 'react-router-dom';
import type { ListingSummaryViewModel } from 'api/types/conversation/ListingSummaryViewModel';
import { MediaService } from 'api/services/MediaService';

interface ListingContextBannerProps {
  summary: ListingSummaryViewModel | null | undefined;
}

const formatPrice = (priceMinor: number): string => {
  if (priceMinor == null || Number.isNaN(priceMinor)) return '';
  return priceMinor.toLocaleString('tr-TR') + ' ₺';
};

/**
 * Persistent listing-context banner rendered at the top of a chat pane.
 * Renders nothing when no listing is linked (e.g. support conversations).
 * See specs/008-messaging-context-ratings US1.
 */
const ListingContextBanner: React.FC<ListingContextBannerProps> = ({ summary }) => {
  if (!summary) return null;

  const unavailable = !summary.isAvailable;
  const title = summary.title || 'İlan';
  const thumb = summary.primaryImageUrl
    ? MediaService.toImageSrc(summary.primaryImageUrl)
    : undefined;

  const titleClass = unavailable ? 'text-muted' : 'fw-semibold';

  const titleNode = (
    <span className={`${titleClass} text-truncate d-block`}>
      {title}
      {unavailable && (
        <span className="badge bg-secondary ms-2">İlan kullanılamıyor</span>
      )}
    </span>
  );

  return (
    <div className="listing-context-banner card border-0 border-bottom rounded-0" style={{ backgroundColor: 'var(--sb-bg)' }}>
      <div className="d-flex align-items-center gap-3 p-2 p-md-3">
        <div
          className="flex-shrink-0 rounded overflow-hidden sb-surface-muted d-flex align-items-center justify-content-center"
          style={{ width: 48, height: 48 }}
        >
          {thumb ? (
            <img
              src={thumb}
              alt={title}
              className="w-100 h-100"
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <i className="fas fa-box text-muted" />
          )}
        </div>
        <div className="flex-grow-1 min-width-0">
          {unavailable ? (
            titleNode
          ) : (
            <Link
              to={`/ilan-detay-login/${summary.id}`}
              className="text-decoration-none text-primary"
            >
              {titleNode}
            </Link>
          )}
          {summary.priceMinor != null && (
            <small className="text-muted">{formatPrice(summary.priceMinor)}</small>
          )}
        </div>
      </div>
    </div>
  );
};

export default ListingContextBanner;
